/**
 * Service for fetching and updating account data from the BFF API.
 */

import type { AccountResponse } from '@shared/models/account-types';
import { extractErrorMessage, makeApiRequest } from '@shared/services/api-client';

export interface AccountApiError {
    message: string;
    status: number;
}

export type FetchAccountResult =
    | { success: true; data: AccountResponse }
    | { success: false; error: AccountApiError };

export type UpdateAccountResult =
    | { success: true; data: AccountResponse }
    | { success: false; error: AccountApiError };

export async function fetchAccountData(): Promise<FetchAccountResult> {
    try {
        const response = await makeApiRequest(
            '/customer/account/managemyaccount',
            { method: 'GET' }
        );
        if (!response.ok) {
            const errorMessage = await extractErrorMessage(response);
            return {
                success: false,
                error: { message: errorMessage, status: response.status }
            };
        }
        const data: AccountResponse = await response.json();
        return { success: true, data };
    } catch (error) {
        return {
            success: false,
            error: {
                message: error instanceof Error ? error.message : 'Failed to fetch account data',
                status: 0
            }
        };
    }
}

export async function updateAccountData(
    fields: Record<string, unknown>
): Promise<UpdateAccountResult> {
    try {
        const response = await makeApiRequest(
            '/customer/account/managemyaccount',
            {
                method: 'PUT',
                body: JSON.stringify(fields)
            }
        );
        if (!response.ok) {
            const errorMessage = await extractErrorMessage(response);
            return {
                success: false,
                error: { message: errorMessage, status: response.status }
            };
        }
        const data: AccountResponse = await response.json();
        return { success: true, data };
    } catch (error) {
        return {
            success: false,
            error: {
                message: error instanceof Error ? error.message : 'Failed to update account data',
                status: 0
            }
        };
    }
}
